import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button, message } from 'antd';
import { DataMapApi } from '@/api/dataMapApi';
import { DataSourceType } from '../../hooks/useInitDataMap';

interface UploadFileModalProps {
  visible: boolean;
  onClose: () => void;
  dataSourceId: string;
  dataSourceName: string;
  onFileUploaded: () => void;
}

const UploadFileModal: React.FC<UploadFileModalProps> = ({ visible, onClose, dataSourceId, dataSourceName, onFileUploaded }) => {
  // 文件上传状态
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  // 文件上传输入框引用
  const fileInputRef = useRef<HTMLInputElement>(null);
  // 上传状态
  const [isUploading, setIsUploading] = useState(false);
  // 表名
  const [tableName, setTableName] = useState('');

  // 当模态框打开时重置状态
  useEffect(() => {
    if (visible) {
      setUploadedFile(null);
      setIsUploading(false);
      setTableName('');
      // 重置文件输入框的值
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [visible]);

  // 处理文件上传
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // 验证文件类型
      if (!file.name.endsWith('.csv')) {
        message.error('仅支持CSV文件，请更换文件');
        return;
      }
      // 验证文件大小（15MB）
      if (file.size > 15 * 1024 * 1024) {
        message.error('文件体积超出限制，请更换文件');
        return;
      }
      setUploadedFile(file);
      // 自动设置表名为文件名（去掉.csv后缀）
      const fileName = file.name.replace('.csv', '');
      setTableName(fileName);
    }
  };

  // 处理完成上传
  const handleCompleteUpload = async () => {
    if (!uploadedFile) {
      message.error('请选择要上传的文件');
      return;
    }

    if (!tableName) {
      message.error('请输入表名');
      return;
    }

    // 验证表名正则表达式：^[a-zA-Z][a-zA-Z0-9_]*$
    const tableNameRegex = /^[a-zA-Z][a-zA-Z0-9_]*$/;
    if (!tableNameRegex.test(tableName)) {
      message.error('表名必须以字母开头，只能包含字母、数字和下划线');
      return;
    }

    try {
      setIsUploading(true);
      // 上传文件，使用用户输入的表名
      const uploadRes = await DataMapApi.uploadDataSources(uploadedFile, dataSourceId, tableName);
      if (uploadRes?.code === 200) {
        message.success('文件上传成功');
        onFileUploaded();
        handleClose();
      } else {
        message.error('文件上传失败');
      }
    } catch (error) {
      message.error('文件上传失败');
      console.error('上传文件错误:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // 关闭模态框并重置状态
  const handleClose = () => {
    setUploadedFile(null);
    setIsUploading(false);
    // 重置文件输入框的值，确保再次选择相同文件时能触发 onChange 事件
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Modal
      title="上传文本文件"
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
      centered
      style={{ zIndex: 1001 }}
    >
      <div className="py-6 px-8">
        {/* 文件上传区域 */}
        <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
          <input 
            type="file" 
            className="hidden" 
            id="file-upload"
            accept=".csv"
            onChange={handleFileUpload}
            ref={fileInputRef}
          />
          <label 
            htmlFor="file-upload"
            className="cursor-pointer"
          >
            <div className="flex flex-col items-center">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19" stroke="#1890ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 12H19" stroke="#1890ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p className="mt-2 text-gray-500">点击此处选择文件上传</p>
            </div>
          </label>
          <p className="mt-4 text-sm text-gray-400">
            仅支持CSV文件，每个文件不超过 15MB。
          </p>
        </div>
        
        {/* 上传文件信息 */}
        {uploadedFile && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm">已选择文件: {uploadedFile.name}</p>
            <p className="text-sm text-gray-500">文件大小: {(uploadedFile.size / 1024).toFixed(2)} KB</p>
          </div>
        )}
        
        {/* 表名输入框 */}
        {uploadedFile && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">表名</label>
            <input 
              type="text"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              placeholder="请输入表名"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">表名必须以字母开头，只能包含字母、数字和下划线</p>
          </div>
        )}
        
        {/* 提示信息 */}
        <p className="text-sm text-gray-500 mb-6">
          仅支持CSV文件，文件不超过15MB
        </p>
        
        <div className="flex justify-end">
          <Button 
            type="primary"
            onClick={handleCompleteUpload}
            disabled={!uploadedFile || !tableName || isUploading}
            loading={isUploading}
          >
            完成
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UploadFileModal;